import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { PlusCircle, UtensilsCrossed, Wine, Users, Store, ChevronRight, Calendar, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { format, parseISO } from 'date-fns';

export default function Dashboard() {
  const { user, authHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentMeals, setRecentMeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, mealsRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers: authHeaders() }),
          axios.get(`${API}/meals?limit=5`, { headers: authHeaders() })
        ]);
        setStats(statsRes.data);
        setRecentMeals(mealsRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [API, authHeaders]);

  const formatDate = (dateStr) => {
    try { return format(parseISO(dateStr), "MMM do, h:mm a"); } 
    catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-lg" style={{ color: '#5E646E' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="pb-nav px-4 md:px-6 max-w-2xl mx-auto fade-in">
      {/* Header */}
      <div className="pt-6 pb-4">
        <p className="text-sm uppercase tracking-wider font-medium" style={{ color: '#5E646E', fontFamily: 'DM Sans, sans-serif' }}>
          Welcome back
        </p>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
          {user?.name || 'Guest'}
        </h1>
      </div>

      {/* Quick Action */}
      <Button
        data-testid="quick-add-meal"
        onClick={() => navigate('/add')}
        className="w-full h-14 rounded-2xl text-lg font-medium mb-6 touch-active gap-3"
        style={{ background: '#005A8D', color: '#FFFFFF' }}
      >
        <PlusCircle className="w-5 h-5" />
        Log a Meal
      </Button>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div data-testid="stat-meals" className="stat-ocean rounded-2xl p-4 text-white">
          <UtensilsCrossed className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats?.total_meals || 0}
          </div>
          <div className="text-sm opacity-80">Total Meals</div>
        </div>
        <div data-testid="stat-covers" className="stat-green rounded-2xl p-4 text-white">
          <UserCheck className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats?.total_covers || 0}
          </div>
          <div className="text-sm opacity-80">Covers Paid</div>
        </div>
        <div data-testid="stat-drinks" className="stat-terra rounded-2xl p-4 text-white">
          <Wine className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats?.drinks_count || 0}
          </div>
          <div className="text-sm opacity-80">Drinks Tracked</div>
        </div>
        <div data-testid="stat-restaurants" className="stat-gold rounded-2xl p-4 text-white">
          <Store className="w-6 h-6 mb-2 opacity-80" />
          <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats?.unique_restaurants || 0}
          </div>
          <div className="text-sm opacity-80">Restaurants</div>
        </div>
      </div>

      {/* Recent Meals */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
            Recent Meals
          </h2>
          {recentMeals.length > 0 && (
            <button
              data-testid="view-all-meals"
              onClick={() => navigate('/history')}
              className="text-sm font-medium flex items-center gap-1"
              style={{ color: '#005A8D' }}
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {recentMeals.length === 0 ? (
          <div className="bg-white rounded-2xl border p-8 text-center" style={{ borderColor: '#E8E6E0' }}>
            <UtensilsCrossed className="w-10 h-10 mx-auto mb-3" style={{ color: '#C76D5B' }} />
            <p className="text-base font-medium" style={{ color: '#1A1D21' }}>No meals logged yet</p>
            <p className="text-sm mt-1" style={{ color: '#5E646E' }}>Tap "Log a Meal" to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentMeals.map((meal, i) => (
              <div
                key={meal.id}
                data-testid={`recent-meal-${i}`}
                onClick={() => navigate('/history')}
                className="bg-white rounded-2xl border p-4 card-hover cursor-pointer"
                style={{ borderColor: '#E8E6E0', animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-medium" style={{ color: '#1A1D21', fontFamily: 'DM Sans, sans-serif' }}>
                      {meal.restaurant_name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Calendar className="w-3.5 h-3.5" style={{ color: '#5E646E' }} />
                      <span className="text-sm" style={{ color: '#5E646E' }}>{formatDate(meal.meal_date)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 mt-0.5" style={{ color: '#C8C6BF' }} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {meal.items.slice(0, 3).map((item, j) => (
                    <Badge
                      key={j}
                      className="text-xs rounded-full px-2.5 py-0.5 font-normal"
                      style={{
                        background: item.item_type === 'drink' ? 'rgba(199, 109, 91, 0.12)' : 'rgba(0, 90, 141, 0.08)',
                        color: item.item_type === 'drink' ? '#C76D5B' : '#005A8D',
                        border: 'none'
                      }}
                    >
                      {item.item_type === 'drink' ? '🍸' : '🍽'} {item.name}
                    </Badge>
                  ))}
                  {meal.items.length > 3 && (
                    <Badge className="text-xs rounded-full px-2.5 py-0.5 font-normal" style={{ background: '#F0EFEA', color: '#5E646E', border: 'none' }}>
                      +{meal.items.length - 3} more
                    </Badge>
                  )}
                </div>
                {meal.companions.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Users className="w-3.5 h-3.5" style={{ color: '#5E646E' }} />
                    <span className="text-sm" style={{ color: '#5E646E' }}>
                      {meal.companions.join(', ')}
                    </span>
                  </div>
                )}
                {(meal.covers || 1) > 1 && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <UserCheck className="w-3.5 h-3.5" style={{ color: '#2E5C55' }} />
                    <span className="text-sm" style={{ color: '#5E646E' }}>
                      {meal.covers} covers
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
